/**
 * The forklift.
 *
 * DA-00-08: the design brief asks for a flat illustration on the sign-in page,
 * the empty states and the 404; the prototype shows only the JPEG wordmark. One
 * drawing, three places, so the product has a character rather than three
 * unrelated blank screens.
 *
 * Flat on purpose — yellow fill, ink outline, no gradients and no shadows. It
 * has to read at 120px on a sign-in card and at 64px inside an empty table, in
 * both themes, and a flat two-colour drawing is the only thing that does all
 * four without a second asset.
 *
 * An inline SVG rather than a file in `public/`. It inherits `currentColor` for
 * the outline, which is how the same drawing works on white and on navy without
 * a dark-mode copy; a linked `<img>` cannot see the page's colours at all. It
 * also costs no request on the one screen somebody sees before anything else
 * has loaded.
 *
 * Decorative by default: `aria-hidden`, no title, and nothing for a screen
 * reader to announce. Every screen that uses it has a real heading beside it,
 * and an illustration read out as "forklift" before that heading is noise. Pass
 * a `title` on the rare screen where the picture carries the meaning.
 */

export interface ForkliftProps {
	/** Rendered size in pixels. The drawing is square. */
	size?: number;
	/**
	 * An accessible name. Omit it — which is the default — and the drawing is
	 * hidden from assistive technology, which is correct beside a heading that
	 * already says what the screen is.
	 */
	title?: string;
	className?: string;
}

export function Forklift({ size = 120, title, className }: ForkliftProps) {
	const labelled = title !== undefined;

	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 120 120"
			fill="none"
			className={className}
			role={labelled ? "img" : undefined}
			aria-hidden={labelled ? undefined : true}
			aria-label={labelled ? title : undefined}
			// The outline follows the text colour, so one drawing works on the
			// light card and on the navy sidebar without a second copy.
			stroke="currentColor"
			strokeWidth={3}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			{/* The load on the forks: two stacked cartons. Drawn first so the
			    mast and forks sit in front of them. */}
			<rect x="14" y="40" width="30" height="24" rx="2" fill="var(--accent)" />
			<rect x="14" y="64" width="30" height="24" rx="2" fill="var(--accent)" />
			<path d="M29 40v24M29 64v24" />

			{/* Mast. */}
			<path d="M52 18v70" />
			<path d="M60 18v70" />
			<path d="M52 18h8" />

			{/* Forks: the vertical carriage and the two tines under the load. */}
			<path d="M52 88H14" />
			<path d="M52 64H44" />

			{/* Body and cab. */}
			<path d="M60 52h26a6 6 0 0 1 6 6v30H60z" fill="var(--accent)" />
			<path d="M68 30h18l8 22H68z" fill="var(--accent)" />
			<path d="M68 30v22M86 30l8 22" />

			{/* Wheels. Filled with the page's own surface so the spokes read in
			    both themes without a second fill colour. */}
			<circle cx="72" cy="94" r="12" fill="var(--bg-surface)" />
			<circle cx="72" cy="94" r="4" fill="currentColor" stroke="none" />
			<circle cx="100" cy="96" r="9" fill="var(--bg-surface)" />
			<circle cx="100" cy="96" r="3" fill="currentColor" stroke="none" />

			{/* Ground. */}
			<path d="M6 108h108" />
		</svg>
	);
}
